import React, { useEffect, useState } from 'react';
import { formatDistanceToNowStrict, format } from 'date-fns';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { challengeApi } from './api';
import { useAuth } from '../../context/AuthContext';

/** Small monospace uppercase label used above titles ("STAGE 3 · …", "PART B"). */
export function Eyebrow({ children, className = '' }) {
    return (
        <p className={`font-mono text-[11px] font-medium uppercase tracking-[0.14em] text-ink-500 ${className}`}>
            {children}
        </p>
    );
}

export function Panel({ children, className = '', ...rest }) {
    return (
        <section className={`rounded-2xl border border-border bg-white p-5 md:p-6 ${className}`} {...rest}>
            {children}
        </section>
    );
}

const STATUS = {
    not_started: { label: 'Not started', className: 'bg-ink-100 text-ink-600', dot: 'bg-ink-400' },
    in_progress: { label: 'In progress', className: 'bg-sky-50 text-sky-800', dot: 'bg-sky-600' },
    submitted: { label: 'Submitted · awaiting mentor', className: 'bg-amber-50 text-amber-800', dot: 'bg-amber-500' },
    changes_requested: { label: 'Changes requested', className: 'bg-red-50 text-red-700', dot: 'bg-red-500' },
    approved: { label: 'Approved', className: 'bg-emerald-50 text-emerald-800', dot: 'bg-emerald-600' },
    locked: { label: 'Locked', className: 'bg-ink-100 text-ink-600', dot: null }
};

export function StatusPill({ status, label, className = '' }) {
    const config = STATUS[status] || STATUS.not_started;
    return (
        <span className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${config.className} ${className}`}>
            {status === 'locked'
                ? <LockClosedIcon className="h-3.5 w-3.5" aria-hidden="true" />
                : <span className={`h-1.5 w-1.5 rounded-full ${config.dot}`} aria-hidden="true" />}
            {label || config.label}
        </span>
    );
}

export function ProgressBar({ percent, className = '' }) {
    const value = Math.max(0, Math.min(100, Number(percent) || 0));
    return (
        <div className={`h-2 w-full overflow-hidden rounded-full bg-ink-100 ${className}`} role="progressbar" aria-valuenow={value} aria-valuemin={0} aria-valuemax={100}>
            <div className="h-full rounded-full bg-brand-700 transition-[width] duration-300" style={{ width: `${value}%` }} />
        </div>
    );
}

/** Re-renders every 30s so "2 min ago" stays honest. */
export function useTick(ms = 30000) {
    const [, setTick] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setTick((n) => n + 1), ms);
        return () => clearInterval(id);
    }, [ms]);
}

export function timeAgo(value) {
    if (!value) return '';
    try {
        const diff = Date.now() - new Date(value).getTime();
        if (diff < 45000) return 'just now';
        return `${formatDistanceToNowStrict(new Date(value))} ago`;
    } catch {
        return '';
    }
}

export function formatDate(value, pattern = 'd MMM yyyy') {
    if (!value) return '—';
    try {
        return format(new Date(value), pattern);
    } catch {
        return '—';
    }
}

export function fileSize(bytes) {
    const n = Number(bytes) || 0;
    if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    if (n >= 1024) return `${Math.round(n / 1024)} KB`;
    return `${n} B`;
}

/** Render **bold** runs in admin-authored copy without HTML injection. */
export function RichText({ text }) {
    if (!text) return null;
    const parts = String(text).split(/(\*\*[^*]+\*\*)/g);
    return (
        <>
            {parts.map((part, i) => (part.startsWith('**') && part.endsWith('**')
                ? <strong key={i} className="font-semibold text-ink-900">{part.slice(2, -2)}</strong>
                : <React.Fragment key={i}>{part}</React.Fragment>))}
        </>
    );
}

/** Evidence files are private, so images load through the API as blobs. */
export function useFileUrl(fileId, enabled = true) {
    const [url, setUrl] = useState(null);
    useEffect(() => {
        if (!fileId || !enabled) return undefined;
        let objectUrl = null;
        let cancelled = false;
        challengeApi.fileBlob(fileId)
            .then((blob) => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(blob);
                setUrl(objectUrl);
            })
            .catch(() => {});
        return () => {
            cancelled = true;
            if (objectUrl) URL.revokeObjectURL(objectUrl);
        };
    }, [fileId, enabled]);
    return url;
}

export async function openFile(fileId) {
    const blob = await challengeApi.fileBlob(fileId);
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/** Admins and mentors share the challenge builder; links follow the viewer's area. */
export function useManageBase() {
    const { navRole } = useAuth();
    return navRole === 'admin'
        ? { base: '/admin/practical-challenges', area: 'Admin' }
        : { base: '/mentor/practical-challenges', area: 'Mentor' };
}

export const READINESS_LABELS = { low: 'Low', developing: 'Developing', good: 'Good', strong: 'Strong' };

export function Toggle({ checked, onChange, label, disabled }) {
    return (
        <label className={`flex items-center justify-between gap-3 py-2 text-sm text-ink-800 ${disabled ? 'opacity-60' : 'cursor-pointer'}`}>
            <span>{label}</span>
            <button
                type="button"
                role="switch"
                aria-checked={checked}
                disabled={disabled}
                onClick={() => onChange(!checked)}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${checked ? 'bg-brand-700' : 'bg-ink-200'}`}
            >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
            </button>
        </label>
    );
}
